import React from 'react';
import { Gauge } from '@mui/x-charts/Gauge';
import { MuiCard } from '@ace/ace-ui-web';
import { getGaugeColor } from '../../utils/gaugeUtils';
import type { TabData } from '../../models/operationsmodels/KpiModels';
import styles from './KpiCardsSection.module.css';

interface KpiCardsSectionProps {
  /** Array of KPI card data to render */
  tabs: TabData[];
  /**
   * When true, renders compact non-interactive cards suitable for the print view.
   * When false (default), renders interactive MuiCard-based cards for the live dashboard.
   */
  isPrintView?: boolean;
  /** Index of the currently selected tab (live view only) */
  selectedTabIndex?: number | null;
  /** Callback fired when a card is clicked (live view only) */
  onCardClick?: (index: number) => void;
}

/**
 * KpiCardsSection
 *
 * Shared component for rendering the row of KPI gauge cards used by both
 * the live Operations dashboard and the print/PDF view.
 *
 * - Live view: interactive MuiCards with selection highlight and hover effects
 * - Print view: compact static cards with print-optimised sizing
 */
const KpiCardsSection: React.FC<KpiCardsSectionProps> = ({
  tabs,
  isPrintView = false,
  selectedTabIndex = null,
  onCardClick,
}): JSX.Element => {
  if (isPrintView) {
    return (
      <div className={styles.printGrid}>
        {tabs.map((tab, index) => {
          const currentKpi = tab.current || 0;
          const targetKpi = tab.target || 1;
          const kpiColor = getGaugeColor(currentKpi, targetKpi);

          return (
            <div
              key={`${tab.label}-${index}`}
              className={styles.printCardItem}>
              <div className={styles.printCardContent}>
                <h3 className={styles.printCardTitle}>{tab.label}</h3>
                <Gauge
                  value={currentKpi}
                  valueMax={targetKpi}
                  width={119}
                  height={119}
                  innerRadius="70%"
                  outerRadius="100%"
                  sx={{
                    '& .MuiGauge-valueArc': { fill: kpiColor },
                    '& .MuiGauge-referenceArc': { fill: '#E0E0E0' },
                    '& .MuiGauge-valueText': { fontSize: '13px', fontWeight: '500' },
                  }}
                  text={({ value, valueMax }) => (tab.hasData ? `${value}/${valueMax}` : '-')}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {tabs.map((tab, index) => {
        const isSelected = selectedTabIndex === index;
        const currentKpi = tab.current || 0;
        const targetKpi = tab.target || 1;
        const kpiColor = getGaugeColor(currentKpi, targetKpi);

        return (
          <div
            key={`${tab.label}-${index}`}
            className={`${styles.cardItem} ${isSelected ? styles.selected : ''}`}
            onClick={() => onCardClick?.(index)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.repeat) {
                return;
              }
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCardClick?.(index);
              }
            }}>
            <MuiCard
              borderRadius={2}
              elevation={1}
              height={207}
              padding={2}
              variant="elevation"
              width={176}>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{tab.label}</h3>
                <Gauge
                  value={currentKpi}
                  valueMax={targetKpi}
                  width={140}
                  height={140}
                  innerRadius="75%"
                  outerRadius="100%"
                  sx={{
                    '& .MuiGauge-valueArc': { fill: kpiColor },
                    '& .MuiGauge-referenceArc': { fill: '#E0E0E0' },
                    '& .MuiGauge-valueText': { fontSize: '18px', fontWeight: '500' },
                  }}
                  text={({ value, valueMax }) => (tab.hasData ? `${value}/${valueMax}` : '-')}
                />
              </div>
            </MuiCard>
          </div>
        );
      })}
    </div>
  );
};

export default KpiCardsSection;
